/**
 * Shared utility functions for community events
 *
 * Timezone model
 * --------------
 * Community events are date-only calendar days (`nextEventDate` / `instanceDate`)
 * plus naive wall-clock time strings (`startTime` / `endTime`). The date is stored
 * at UTC midnight of the chosen day (e.g. "2026-07-31" -> 2026-07-31T00:00:00.000Z).
 *
 * All events are treated as Eastern time (America/New_York) and displayed in Eastern
 * to every viewer, regardless of their device timezone. Concretely:
 *   - The calendar DAY is read from the value's UTC components and formatted in UTC,
 *     so it never shifts across viewer timezones (formatting it in Eastern would be
 *     wrong: UTC-midnight Jul 31 is Jul 30 in Eastern).
 *   - The event's true instant (for countdown / status / past-vs-upcoming) is the
 *     Eastern wall-clock time on that calendar day, converted to a real UTC instant.
 */

/**
 * Canonical timezone for all community events.
 */
export const EVENT_TIME_ZONE = "America/New_York";

/**
 * Convert time string (HH:MM or h:mm AM/PM) to 24-hour format
 * @param {string} timeStr - Time string in either format
 * @returns {string} Time in HH:MM format
 */
export function parseTimeTo24Hour(timeStr) {
  if (!timeStr) return "00:00";

  // Already in 24-hour format (HH:MM)
  if (/^\d{2}:\d{2}$/.test(timeStr)) {
    return timeStr;
  }

  // Parse 12-hour format (e.g., "01:00 PM")
  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3].toUpperCase();

    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;

    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  }

  return timeStr;
}

/**
 * Format time for display (24-hour to 12-hour)
 * @param {string} timeStr - Time string
 * @returns {string} Time in 12-hour format with AM/PM
 */
export function formatTimeForDisplay(timeStr) {
  if (!timeStr) return "";

  // If already in 12-hour format, return as is
  if (/AM|PM/i.test(timeStr)) return timeStr;

  // Convert 24-hour to 12-hour
  const [hours, minutes] = timeStr.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;

  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
}

// ==========================================
// Timezone helpers (Eastern / America/New_York)
// ==========================================

/**
 * Get a timezone's offset (in minutes east of UTC) at a given instant.
 * Positive = east of UTC. America/New_York returns negative values
 * (e.g. -240 during EDT, -300 during EST).
 * @param {string} timeZone - IANA timezone name
 * @param {Date} date - Instant at which to measure the offset
 * @returns {number} Offset in minutes
 */
function getTimeZoneOffsetMinutes(timeZone, date) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = dtf.formatToParts(date).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
  return (asUTC - date.getTime()) / 60000;
}

/**
 * Extract the calendar day the admin picked from a stored event date.
 * The value is stored at UTC midnight, so its UTC components are the exact day,
 * immune to the viewer's timezone.
 * @param {string|Date} dateValue - Stored event date
 * @returns {{year:number, month:number, day:number}|null} 0-indexed month
 */
export function getEventCalendarParts(dateValue) {
  if (!dateValue) return null;
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return null;
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth(),
    day: d.getUTCDate(),
  };
}

/**
 * Format the event's calendar day for display.
 * Formats in UTC so the day never shifts across viewer timezones.
 * @param {string|Date} dateValue - Stored event date
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date (empty string if no/invalid date)
 */
export function formatEventDate(
  dateValue,
  options = { month: "short", day: "numeric" }
) {
  if (!dateValue) return "";
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { timeZone: "UTC", ...options });
}

/**
 * Build the true UTC instant for an event: the Eastern wall-clock `startTime`
 * on the event's calendar day. Use this for countdown / status / past-vs-upcoming
 * comparisons so they are correct regardless of the viewer's device timezone.
 * @param {string|Date} dateValue - Stored event date
 * @param {string} startTime - Wall-clock time string (Eastern)
 * @returns {Date|null}
 */
export function getEventInstant(dateValue, startTime) {
  const parts = getEventCalendarParts(dateValue);
  if (!parts) return null;
  const time24 = parseTimeTo24Hour(startTime);
  const [hours, minutes] = time24.split(":").map(Number);

  // Treat the wall-clock time as UTC, then correct by Eastern's offset at that
  // moment to recover the real instant.
  const naiveUTC = Date.UTC(
    parts.year,
    parts.month,
    parts.day,
    hours || 0,
    minutes || 0
  );
  const offsetMin = getTimeZoneOffsetMinutes(EVENT_TIME_ZONE, new Date(naiveUTC));
  return new Date(naiveUTC - offsetMin * 60000);
}

/**
 * Get the Eastern timezone abbreviation ("EST" or "EDT") for an event,
 * correct for the event's own date (EDT in summer, EST in winter).
 * @param {string|Date} dateValue - Stored event date
 * @param {string} startTime - Wall-clock time string (Eastern)
 * @returns {string} "EST", "EDT", or "ET" as a fallback
 */
export function getEventZoneLabel(dateValue, startTime) {
  const instant =
    getEventInstant(dateValue, startTime) ||
    (dateValue ? new Date(dateValue) : new Date());
  if (isNaN(instant.getTime())) return "ET";
  const part = new Intl.DateTimeFormat("en-US", {
    timeZone: EVENT_TIME_ZONE,
    timeZoneName: "short",
  })
    .formatToParts(instant)
    .find((p) => p.type === "timeZoneName");
  return part?.value || "ET";
}

/** Get today's date in Eastern time as calendar parts (0-indexed month). */
function getEasternTodayParts() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: EVENT_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .formatToParts(new Date())
    .reduce((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});
  return {
    year: Number(parts.year),
    month: Number(parts.month) - 1,
    day: Number(parts.day),
  };
}

/**
 * Get event start datetime as a Date object (real Eastern instant).
 * @param {string} nextEventDate - Event date
 * @param {string} startTime - Time string
 * @returns {Date|null} Date object or null
 */
export function getEventStartDateTime(nextEventDate, startTime) {
  return getEventInstant(nextEventDate, startTime);
}

/**
 * Calculate countdown to event
 * @param {string} nextEventDate - ISO date string
 * @param {string} startTime - Time string
 * @returns {Object|null} Countdown object with days, hours, minutes, isLive
 */
export function calculateCountdown(nextEventDate, startTime) {
  const eventDateTime = getEventStartDateTime(nextEventDate, startTime);
  if (!eventDateTime) return null;

  const now = new Date();
  const diff = eventDateTime.getTime() - now.getTime();

  if (diff <= 0) {
    return { isLive: true, days: 0, hours: 0, minutes: 0 };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  return { isLive: false, days, hours, minutes };
}

/**
 * Generate Google Calendar URL for an event
 * @param {Object} event - Event object with title, description, nextEventDate, startTime, endTime, meetingLink
 * @returns {string|null} Google Calendar URL or null
 */
export function generateGoogleCalendarUrl(event) {
  const { title, description, nextEventDate, startTime, endTime, meetingLink } = event;

  if (!nextEventDate) return null;

  // Read the calendar day from UTC components (the day the admin picked)
  const parts = getEventCalendarParts(nextEventDate);
  if (!parts) return null;
  const year = parts.year;
  const month = (parts.month + 1).toString().padStart(2, "0");
  const day = parts.day.toString().padStart(2, "0");

  // Parse times to 24-hour format
  const start24 = parseTimeTo24Hour(startTime);
  const end24 = parseTimeTo24Hour(endTime);

  // Format for Google Calendar: YYYYMMDDTHHmmss (interpreted in ctz below)
  const startDateTime = `${year}${month}${day}T${start24.replace(":", "")}00`;
  const endDateTime = `${year}${month}${day}T${end24.replace(":", "")}00`;

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title || "Community Event",
    dates: `${startDateTime}/${endDateTime}`,
    details: description || "",
    location: meetingLink || "",
    ctz: EVENT_TIME_ZONE,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Generate Google Calendar URL for a session (recurring event instance)
 * @param {Object} session - Session/instance object with instanceDate, startTime, endTime, title, description, meetingLink
 * @param {string} parentTitle - Fallback title from parent event
 * @returns {string|null} Google Calendar URL or null
 */
export function generateSessionCalendarUrl(session, parentTitle) {
  const dateField = session.instanceDate || session.nextEventDate;
  if (!dateField) return null;

  const parts = getEventCalendarParts(dateField);
  if (!parts) return null;
  const year = parts.year;
  const month = (parts.month + 1).toString().padStart(2, "0");
  const day = parts.day.toString().padStart(2, "0");

  const start24 = parseTimeTo24Hour(session.startTime);
  const end24 = parseTimeTo24Hour(session.endTime);

  const startDateTime = `${year}${month}${day}T${start24.replace(":", "")}00`;
  const endDateTime = `${year}${month}${day}T${end24.replace(":", "")}00`;

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: session.title || parentTitle || "Community Event",
    dates: `${startDateTime}/${endDateTime}`,
    details: session.description || "",
    location: session.meetingLink || "",
    ctz: EVENT_TIME_ZONE,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Format date and time for display (with Eastern zone label)
 * @param {string} dateString - ISO date string
 * @param {string} startTime - Start time string
 * @param {string} endTime - End time string
 * @returns {string} Formatted date/time string, e.g. "Jul 31, 1:30 PM - 2:30 PM EDT"
 */
export function formatEventDateTime(dateString, startTime, endTime) {
  if (!dateString) return "-";
  const monthDay = formatEventDate(dateString, {
    month: "short",
    day: "numeric",
  });
  const displayStart = formatTimeForDisplay(startTime);
  const displayEnd = formatTimeForDisplay(endTime);
  const timeRange = [displayStart, displayEnd].filter(Boolean).join(" - ");
  if (!timeRange) return monthDay;
  const zone = getEventZoneLabel(dateString, startTime);
  return `${monthDay}, ${timeRange} ${zone}`;
}

/**
 * Get the time-based status of an event
 * @param {string} eventDate - ISO date string (nextEventDate or instanceDate)
 * @param {string} startTime - Start time string
 * @param {string} endTime - End time string
 * @returns {{ key: string, label: string }} Status object with key and display label
 *   key is one of: "live", "completed", "today", "tomorrow", "this-week", "upcoming"
 */
export function getEventStatus(eventDate, startTime, endTime) {
  if (!eventDate) return { key: "upcoming", label: "Upcoming" };

  const now = new Date();

  // Build start and end instants in Eastern time
  const startDT = getEventInstant(eventDate, startTime);
  if (!startDT) return { key: "upcoming", label: "Upcoming" };

  let endDT = getEventInstant(eventDate, endTime) || startDT;
  // If end time is before/equal start time, assume it crosses midnight
  if (endDT <= startDT) {
    endDT = new Date(endDT.getTime() + 24 * 60 * 60 * 1000);
  }

  // Currently happening
  if (now >= startDT && now <= endDT) {
    return { key: "live", label: "Live Now" };
  }

  // Already finished
  if (now > endDT) {
    return { key: "completed", label: "Completed" };
  }

  // Event is in the future — compare Eastern calendar days
  const evParts = getEventCalendarParts(eventDate);
  const todayParts = getEasternTodayParts();
  const evMidnight = Date.UTC(evParts.year, evParts.month, evParts.day);
  const todayMidnight = Date.UTC(
    todayParts.year,
    todayParts.month,
    todayParts.day
  );
  const diffDays = Math.round((evMidnight - todayMidnight) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return { key: "today", label: "Today" };
  }

  if (diffDays === 1) {
    return { key: "tomorrow", label: "Tomorrow" };
  }

  if (diffDays <= 7) {
    return { key: "this-week", label: "This Week" };
  }

  return { key: "upcoming", label: "Upcoming" };
}

/**
 * Format date for short display (e.g., "Jul 15")
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export function formatShortDate(dateString) {
  if (!dateString) return "";
  return formatEventDate(dateString, { month: "short", day: "numeric" });
}

/**
 * Format full date with day of week
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date with weekday
 */
export function formatFullDate(dateString) {
  if (!dateString) return "";
  return formatEventDate(dateString, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}
